import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { categoryIdFromParam, categoryParamFromId } from "./categories";
import { DEFAULT_QUOTE_LANGUAGE } from "../constants/languages";
import { normalizeLanguageCode } from "./language";

// Filters, sort and page live in the URL query string (not component state)
// so every view has its own shareable link, survives refresh, and works with
// the browser's Back/Forward buttons. Changing any filter drops ?page so the
// new result set starts on page 1; values equal to the default are omitted to
// keep links short.

function writeParam(params: URLSearchParams, key: string, value: string | null) {
  if (value === null || value === "") params.delete(key);
  else params.set(key, value);
}

export function useUrlParam<T extends string>(
  key: string,
  defaultValue: T,
  allowed?: readonly T[]
): [T, (value: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get(key);
  const value = raw !== null && (!allowed || allowed.includes(raw as T)) ? (raw as T) : defaultValue;

  const setValue = useCallback(
    (next: T) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        writeParam(params, key, next === defaultValue ? null : next);
        params.delete("page");
        return params;
      });
    },
    [key, defaultValue, setSearchParams]
  );

  return [value, setValue];
}

export function parsePageParam(raw: string | null): number {
  const page = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(page) && page > 0 ? page : 1;
}

export function useUrlPage(): [number, (page: number) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parsePageParam(searchParams.get("page"));

  const setPage = useCallback(
    (next: number) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        writeParam(params, "page", next > 1 ? String(next) : null);
        return params;
      });
    },
    [setSearchParams]
  );

  return [page, setPage];
}

// ?category=<slug>. Returns the category id as a string ("all" when unset),
// matching what CategorySidebar and the item filters already expect, plus a
// href builder so sidebar entries render as real links.
export function useUrlCategory(): [
  string,
  (categoryId: string | number) => void,
  (categoryId: string | number) => string
] {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = categoryIdFromParam(searchParams.get("category"));

  const withCategory = useCallback((prev: URLSearchParams, categoryId: string | number) => {
    const params = new URLSearchParams(prev);
    writeParam(params, "category", categoryParamFromId(categoryId));
    params.delete("page");
    return params;
  }, []);

  const setCategory = useCallback(
    (categoryId: string | number) => setSearchParams((prev) => withCategory(prev, categoryId)),
    [setSearchParams, withCategory]
  );

  const getCategoryHref = useCallback(
    (categoryId: string | number) => `?${withCategory(searchParams, categoryId).toString()}`,
    [searchParams, withCategory]
  );

  return [activeCategory, setCategory, getCategoryHref];
}

// ?lang=<code> - the language quotes are shown in (see QUOTE_LANGUAGES).
// Missing or unknown codes fall back to English. Changing it keeps the
// current page, since it only changes how the same items are displayed.
export function languageFromParam(param: string | null | undefined): string {
  return normalizeLanguageCode(param) ?? DEFAULT_QUOTE_LANGUAGE;
}

// The third value is whether ?lang= is actually in the URL. Without it, each
// quote is shown in the language it was written in; with it (even "en"),
// every quote is shown in that language.
export function useUrlLanguage(): [string, (code: string) => void, boolean] {
  const [searchParams, setSearchParams] = useSearchParams();
  const language = languageFromParam(searchParams.get("lang"));
  const isExplicit = searchParams.has("lang");

  const setLanguage = useCallback(
    (code: string) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("lang", languageFromParam(code));
        return params;
      });
    },
    [setSearchParams]
  );

  return [language, setLanguage, isExplicit];
}

// Adds ?lang= to a URL that doesn't carry one yet, so every shared link
// states its language explicitly (English when none was chosen).
export function withLanguageParam(url: string, language: string = DEFAULT_QUOTE_LANGUAGE): string {
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.set("lang", languageFromParam(parsed.searchParams.get("lang") ?? language));
  return parsed.toString();
}
